import { FieldType, mapToFieldType } from "@smart/bridge-types-basic";
import { toSentenceCase } from "@smart/itops-utils-basic";

import { ContactType, contactTypes } from "./roles";
import {
  GqlFieldValues,
  GqlGroupValues,
  MatterField,
  NewFieldOptions,
} from "../types";

export const mapFieldType = (matterField: MatterField): FieldType | undefined =>
  mapToFieldType(matterField.type);

export const mapFieldOptions = ({
  possibleValues,
}: MatterField): Pick<NewFieldOptions, "options" | "allowCustomResponse"> => {
  if (!possibleValues) return {};
  return {
    options: possibleValues
      .filter((v) => !!v)
      .map((v) => ({ label: v, value: v })),
    allowCustomResponse: possibleValues.includes(""),
  };
};

export const getLabelText = ({
  name,
  displayName,
  details,
}: {
  name: string;
  displayName?: string | null;
  details?: string | null;
}) => {
  const representativeName = displayName || name;
  const label = details
    ? representativeName.replace(`${details}/`, "")
    : representativeName;

  return toSentenceCase(label);
};

const isContactDetailsGroupField = (
  groupMatterField?: GqlGroupValues["field"] | null,
) =>
  !!groupMatterField &&
  groupMatterField.type === "Role" &&
  contactTypes.includes(groupMatterField.name as ContactType);

export const buildMappedPathParts = ({
  layout,
  field,
  groupMatterField,
  matterFields,
}: {
  layout: NonNullable<GqlFieldValues["layout"]>;
  field: NonNullable<GqlFieldValues["field"]>;
  groupMatterField?: GqlGroupValues["field"] | null;
  matterFields: MatterField[];
}) => {
  const pathParts = [
    layout.parentName && toSentenceCase(layout.parentName),
    toSentenceCase(layout.displayName || layout.name),
  ];

  const addFieldPath = (
    parts: (string | null | undefined)[],
    inputField:
      | { displayName?: string | null; name: string }
      | null
      | undefined,
  ) => {
    if (!inputField) return;

    const matterField = matterFields.find(
      (mf) => mf.name.toLowerCase() === inputField.name.toLowerCase(),
    );
    const displayMatterField = matterField || inputField;

    const nameSegments = displayMatterField.name.split("/");

    if (
      nameSegments.length > 1 &&
      nameSegments[0] !== "Matter" &&
      nameSegments[0] !== "matterType"
    ) {
      parts.push(toSentenceCase(nameSegments[0]));
    }

    const fieldDisplayName =
      displayMatterField.displayName || displayMatterField.name;
    const displayParts = fieldDisplayName
      .split("/")
      .map((p: string) => toSentenceCase(p));

    if (
      nameSegments.length > 1 &&
      displayParts[0] === toSentenceCase(nameSegments[0])
    ) {
      displayParts.shift();
    }

    parts.push(...displayParts);
  };

  // A "contactDetails" group's field is only the contact type (person or company), not a mapped path.
  if (!isContactDetailsGroupField(groupMatterField)) {
    addFieldPath(pathParts, groupMatterField);
  }
  addFieldPath(pathParts, field);

  return pathParts;
};
